import type { CalendarEvent } from '../../types'
import {
  WEEKDAYS_SHORT,
  getISOWeek,
  getMonthGrid,
  isToday,
  formatTime,
} from '../../utils/dateUtils'

interface MonthViewProps {
  year: number
  month: number
  eventsOnDay: (d: Date) => CalendarEvent[]
  onSelectDay: (d: Date) => void
  onSelectWeek: (d: Date) => void
  onSelectEvent: (e: CalendarEvent) => void
}

export function MonthView({
  year,
  month,
  eventsOnDay,
  onSelectDay,
  onSelectWeek,
  onSelectEvent,
}: MonthViewProps) {
  const grid = getMonthGrid(year, month)
  const weeks: Date[][] = []
  for (let i = 0; i < grid.length; i += 7) {
    weeks.push(grid.slice(i, i + 7))
  }

  return (
    <div className="month-view">
      <div className="month-view__weekdays">
        <div className="month-view__kw" title="Kalenderwoche">
          KW
        </div>
        {WEEKDAYS_SHORT.map((w) => (
          <div key={w} className="month-view__wd">
            {w}
          </div>
        ))}
      </div>
      <div className="month-view__rows">
        {weeks.map((weekDays, wi) => {
          const weekAnchor = weekDays[0]
          const kw = getISOWeek(weekAnchor)
          return (
            <div key={wi} className="month-view__weekrow">
              <button
                type="button"
                className="month-view__weeknum"
                onClick={() => onSelectWeek(weekAnchor)}
                title={`Woche ${kw} öffnen`}
                aria-label={`Kalenderwoche ${kw} — Wochenansicht öffnen`}
              >
                {kw}
              </button>
              {weekDays.map((d, di) => {
                const inMonth = d.getMonth() === month
                const today = isToday(d)
                const dayEvents = eventsOnDay(d)
                return (
                  <div
                    key={di}
                    className={[
                      'month-cell',
                      !inMonth ? 'month-cell--muted' : '',
                      today ? 'month-cell--today' : '',
                      dayEvents.length ? 'month-cell--events' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => onSelectDay(d)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter' || ev.key === ' ') {
                        ev.preventDefault()
                        onSelectDay(d)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    title="Tagesansicht öffnen"
                    aria-label={`${d.getDate()}. — Tagesansicht öffnen`}
                  >
                    <div className="month-cell__day">{d.getDate()}</div>
                    <div className="month-cell__events">
                      {dayEvents.slice(0, 3).map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          className="event-chip"
                          style={{ borderLeftColor: e.color, color: e.color }}
                          onClick={(ev) => {
                            ev.stopPropagation()
                            onSelectEvent(e)
                          }}
                          title={`${formatTime(e.start)} ${e.title}`}
                        >
                          <span className="event-chip__time">
                            {formatTime(e.start)}
                          </span>
                          <span className="event-chip__title">{e.title}</span>
                        </button>
                      ))}
                      {dayEvents.length > 3 && (
                        <span className="month-cell__more">
                          +{dayEvents.length - 3} weitere
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}
